/**
 * 消息（ChatMessage）仓储：CRUD + 按 `createdAt` 倒序分页。
 *
 * 排序与游标都建立在 `[conversationId+createdAt]` 复合索引的语义之上，
 * 同一毫秒的消息用 id 兜底排序，保证翻页不重不漏。
 */

import Dexie, { type Table } from 'dexie';

import { getDb, type ZxTalkDatabase } from '../db';
import { filterAssistantMessage } from '../content/filterGeneratedLinks';
import { newId } from '../ids';
import type { ChatMessage, Role } from '../types';
import { decodeCursor, encodeCursor, normalizeLimit, type Page, type PageQuery } from './pagination';

/** 消息按会话 + 时间分页所用的复合索引名（见 `db.ts` 的 stores）。 */
const MESSAGE_CONVERSATION_INDEX = '[conversationId+createdAt]';

/**
 * 进程内、按「数据库实例 + 会话」隔离的最后一个自动时间戳。
 * 用于在同一会话内保证 `createMessage` 自动时间戳严格递增，
 * 避免同一毫秒连写时因 `createdAt` 相同导致按 id 兜底排序而乱序。
 * 按库实例隔离：换库（测试隔离 / 多空间）后自动递增状态不互相污染，且无需手动清理。
 */
const lastCreatedAtByConversation = new WeakMap<ZxTalkDatabase, Map<string, number>>();

export interface CreateMessageInput {
  id?: string;
  conversationId: string;
  role: Role;
  content: string;
  createdAt?: number;
  reasoning?: string;
  attachments?: ChatMessage['attachments'];
  images?: string[];
  audioUrl?: string;
  roleVoicePending?: boolean;
  status?: ChatMessage['status'];
  imageJob?: ChatMessage['imageJob'];
  readAt?: number;
}

export interface MessagePageQuery extends PageQuery {
  conversationId: string;
  /** true = 时间升序（聊天记录从上到下）；默认 false = 时间倒序（最新在前）。 */
  ascending?: boolean;
}

function table(): Table<ChatMessage, string> {
  return getDb().messages;
}

export function createMessage(input: CreateMessageInput): ChatMessage {
  return filterAssistantMessage({
    id: input.id ?? newId(),
    conversationId: input.conversationId,
    role: input.role,
    content: input.content,
    createdAt: nextCreatedAt(input.conversationId, input.createdAt),
    status: input.status ?? 'done',
    ...(input.reasoning !== undefined ? { reasoning: input.reasoning } : {}),
    ...(input.attachments !== undefined ? { attachments: input.attachments } : {}),
    ...(input.images !== undefined ? { images: input.images } : {}),
    ...(input.audioUrl !== undefined ? { audioUrl: input.audioUrl } : {}),
    ...(input.roleVoicePending !== undefined ? { roleVoicePending: input.roleVoicePending } : {}),
    ...(input.imageJob !== undefined ? { imageJob: input.imageJob } : {}),
    ...(input.readAt !== undefined ? { readAt: input.readAt } : {}),
  });
}

/**
 * 计算一条新消息的 `createdAt`。
 * - 显式传入的时间戳（导入 / 迁移场景）原样尊重，不改写；
 * - 未显式传入时取 `Math.max(Date.now(), last + 1)`，保证同一会话内严格递增。
 */
function nextCreatedAt(conversationId: string, explicit: number | undefined): number {
  if (explicit !== undefined) return explicit;
  const db = getDb();
  let perConversation = lastCreatedAtByConversation.get(db);
  if (perConversation === undefined) {
    perConversation = new Map<string, number>();
    lastCreatedAtByConversation.set(db, perConversation);
  }
  const last = perConversation.get(conversationId);
  const next = last === undefined ? Date.now() : Math.max(Date.now(), last + 1);
  perConversation.set(conversationId, next);
  return next;
}

/** 新增或按 id 覆盖保存（幂等）。 */
export async function putMessage(message: ChatMessage): Promise<string> {
  return table().put(filterAssistantMessage(message));
}

export async function createAndSaveMessage(input: CreateMessageInput): Promise<ChatMessage> {
  const message = createMessage(input);
  await putMessage(message);
  return message;
}

export async function getMessage(id: string): Promise<ChatMessage | undefined> {
  const message = await table().get(id);
  return message ? filterAssistantMessage(message) : undefined;
}

/** 会话内全部消息，按 `createdAt` 升序（同刻用 id 升序）。 */
export async function listMessages(conversationId: string): Promise<ChatMessage[]> {
  const items = await table().where('conversationId').equals(conversationId).toArray();
  return items.map(filterAssistantMessage).sort(compareAsc);
}

/** All messages, used by the contact inbox and client-side automation. */
export async function listAllMessages(): Promise<ChatMessage[]> {
  return (await table().toArray()).map(filterAssistantMessage);
}

function compareAsc(a: ChatMessage, b: ChatMessage): number {
  return a.createdAt - b.createdAt || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);
}

/**
 * 消息分页：直接走 `[conversationId+createdAt]` 复合索引的游标查询，不再全量拉取后切片。
 * - 默认 `ascending: false`：按时间倒序（最新在前），用于「上滑加载更早消息」；
 * - `ascending: true`：按时间升序，用于一次性渲染完整会话。
 *
 * 复合索引在 `createdAt` 相同时按主键（id）升序排列，`reverse()` 之后即为 id 倒序，
 * 与 `isAfterCursor` 的兜底比较规则一致，因此翻页不重不漏；
 * `hasMore` 由「多取一条」判断，无需额外的 count 查询。
 */
export async function listMessagesPage(query: MessagePageQuery): Promise<Page<ChatMessage>> {
  const limit = normalizeLimit(query.limit);
  const ascending = query.ascending ?? false;
  const cursor = query.cursor ? decodeCursor(query.cursor) : null;

  const indexRange = getDb()
    .messages.where(MESSAGE_CONVERSATION_INDEX)
    .between(
      [query.conversationId, Dexie.minKey],
      [query.conversationId, Dexie.maxKey],
      true,
      true,
    );
  const ordered = ascending ? indexRange : indexRange.reverse();
  const scoped = cursor
    ? ordered.filter((message) => isAfterCursor(message, cursor, ascending))
    : ordered;

  const rows = await scoped.limit(limit + 1).toArray();
  const hasMore = rows.length > limit;
  const items = hasMore ? rows.slice(0, limit) : rows;
  const last = items[items.length - 1];

  return {
    items: items.map(filterAssistantMessage),
    hasMore,
    nextCursor: hasMore && last ? encodeCursor(last.createdAt, last.id) : null,
  };
}

function isAfterCursor(
  message: ChatMessage,
  cursor: { createdAt: number; id: string },
  ascending: boolean,
): boolean {
  if (ascending) {
    return (
      message.createdAt > cursor.createdAt ||
      (message.createdAt === cursor.createdAt && message.id > cursor.id)
    );
  }
  return (
    message.createdAt < cursor.createdAt ||
    (message.createdAt === cursor.createdAt && message.id < cursor.id)
  );
}

export async function updateMessage(
  id: string,
  patch: Partial<Omit<ChatMessage, 'id' | 'conversationId'>>,
): Promise<ChatMessage | undefined> {
  const existing = await getMessage(id);
  if (!existing) return undefined;
  const next = filterAssistantMessage({ ...existing, ...patch, id, conversationId: existing.conversationId });
  await putMessage(next);
  return next;
}

export async function deleteMessage(id: string): Promise<void> {
  await table().delete(id);
}

export async function countMessages(conversationId: string): Promise<number> {
  return table().where('conversationId').equals(conversationId).count();
}

/** Mark every unread assistant message in a conversation as read. */
export async function markConversationRead(
  conversationId: string,
  readAt = Date.now(),
): Promise<number> {
  return getDb().transaction('rw', getDb().messages, async () => {
    const unread = await table()
      .where('conversationId')
      .equals(conversationId)
      .filter((message) => message.role === 'assistant' && message.readAt === 0)
      .toArray();
    if (unread.length === 0) return 0;
    await table().bulkPut(unread.map((message) => ({ ...message, readAt })));
    return unread.length;
  });
}

/** 会话列表需要的生成中会话集合；后台任务未 ACK 前也保持忙碌状态。 */
export async function listGeneratingConversationIds(): Promise<string[]> {
  const messages = await table().toArray();
  return [...new Set(messages
    .filter(message => message.status !== 'error' && (
      ['pending', 'streaming'].includes(message.status) || (message.backgroundJob !== undefined && !message.backgroundJob.saved)
    ))
    .map(message => message.conversationId))];
}

export async function clearMessages(): Promise<void> {
  await table().clear();
}
